import React from 'react';
import { Helmet } from 'react-helmet';
import '../../styles/theme.css';

const HIPAA = () => {
  return (
    <>
      <Helmet>
        <title>HIPAA Compliance | OptiBill Analytics</title>
        <meta name="description" content="OptiBill Analytics HIPAA Compliance Statement - Our commitment to protecting patient privacy and maintaining the highest standards of data security." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <div className="legal-shell">
        <div className="legal-container">
          <div className="legal-card">
            {/* Header */}
            <div className="legal-header">
              <h1 className="text-navy fw-bold" style={{ fontSize: 36, marginBottom: 12 }}>HIPAA Compliance Statement</h1>
              <p style={{ color: '#5A6478', fontSize: 16 }}>Last Updated: March 21, 2026</p>
            </div>

            {/* Introduction */}
            <section style={{ marginBottom: 32 }}>
              <div className="cta-panel" style={{ padding: 24, marginBottom: 24, textAlign: 'left' }}>
                <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>Our HIPAA Commitment</h3>
                <p style={{ fontSize: 16, lineHeight: 1.6, margin: 0 }}>
                  OptiBill Analytics is committed to maintaining full compliance with the Health Insurance Portability and Accountability Act (HIPAA) and its implementing regulations. We understand the critical importance of protecting patient privacy and maintaining the confidentiality, integrity, and availability of Protected Health Information (PHI).
                </p>
              </div>
              <p style={{ color: '#5A6478', fontSize: 17, lineHeight: 1.7 }}>
                As a Business Associate serving healthcare providers, we have implemented comprehensive policies, procedures, and safeguards to ensure HIPAA compliance in all aspects of our medical billing and revenue cycle management services.
              </p>
            </section>

            {/* Section 1 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>1. HIPAA Overview and Our Role</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                The Health Insurance Portability and Accountability Act (HIPAA) establishes national standards for protecting sensitive patient health information. As your medical billing partner, OptiBill Analytics functions as a <strong>Business Associate</strong> under HIPAA regulations.
              </p>
              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>What is a Business Associate?</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                A Business Associate is a person or entity that performs functions or activities on behalf of a covered entity (healthcare provider) that involve access to Protected Health Information. Our billing and RCM services require us to access, use, and disclose PHI, making us a Business Associate.
              </p>
              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>Our Obligations</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                As a Business Associate, we are required to comply with the HIPAA Privacy Rule, Security Rule, and Breach Notification Rule. We execute a Business Associate Agreement (BAA) with each client, which legally binds us to safeguard PHI and use it only for permitted purposes.
              </p>
            </section>

            {/* Section 2 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>2. HIPAA Privacy Rule Compliance</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 16 }}>
                The Privacy Rule establishes standards for the protection of PHI. Our privacy practices include:
              </p>
              <div className="legal-callout" style={{ marginBottom: 16 }}>
                <h3 className="text-navy fw-semibold" style={{ fontSize: 18, marginBottom: 12 }}>Minimum Necessary Standard</h3>
                <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                  We access, use, and disclose only the minimum amount of PHI necessary to accomplish our billing and RCM functions. Our systems are configured to limit data access based on job roles and specific tasks.
                </p>
              </div>
              <div className="legal-callout" style={{ marginBottom: 16 }}>
                <h3 className="text-navy fw-semibold" style={{ fontSize: 18, marginBottom: 12 }}>Permitted Uses and Disclosures</h3>
                <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 8 }}>
                  We use and disclose PHI only for:
                </p>
                <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                  <li>Processing insurance claims and billing services</li>
                  <li>Payment activities, including posting payments and managing A/R</li>
                  <li>Healthcare operations authorized by our clients</li>
                  <li>Compliance with legal requirements</li>
                  <li>Other purposes specifically authorized in our BAA</li>
                </ul>
              </div>
              <div className="legal-callout">
                <h3 className="text-navy fw-semibold" style={{ fontSize: 18, marginBottom: 12 }}>Individual Rights</h3>
                <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                  We support our clients in fulfilling patient rights under HIPAA, including the right to access, amend, and receive an accounting of disclosures of their PHI. Patients should contact their healthcare provider directly to exercise these rights.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>3. HIPAA Security Rule Compliance</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 16 }}>
                The Security Rule requires administrative, physical, and technical safeguards to protect electronic PHI (ePHI). Our comprehensive security program includes:
              </p>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>3.1 Administrative Safeguards</h3>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24, marginBottom: 16 }}>
                <li><strong>Security Management Process:</strong> Regular risk assessments, risk management strategies, sanction policies, and information system activity reviews</li>
                <li><strong>Workforce Training:</strong> Comprehensive HIPAA training for all employees, with annual refresher courses and role-specific training</li>
                <li><strong>Security Officer:</strong> Designated Privacy and Security Officer responsible for HIPAA compliance oversight</li>
                <li><strong>Access Management:</strong> Policies governing workforce access to ePHI based on job roles and least privilege principles</li>
                <li><strong>Incident Response:</strong> Documented procedures for identifying, responding to, and mitigating security incidents</li>
                <li><strong>Business Associate Management:</strong> Due diligence and contract requirements for our subcontractors</li>
              </ul>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>3.2 Physical Safeguards</h3>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24, marginBottom: 16 }}>
                <li><strong>Facility Access Controls:</strong> Secure data centers with restricted access, badge systems, and visitor logs</li>
                <li><strong>Workstation Security:</strong> Policies for workstation use, positioning, and automatic screen locks</li>
                <li><strong>Device and Media Controls:</strong> Procedures for disposal, reuse, and accountability of hardware and electronic media containing ePHI</li>
                <li><strong>Clean Desk Policy:</strong> Requirements to secure physical documents and storage media</li>
              </ul>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>3.3 Technical Safeguards</h3>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                <li><strong>Access Controls:</strong> Unique user IDs, automatic logoff, encryption, and emergency access procedures</li>
                <li><strong>Audit Controls:</strong> Logging and monitoring of system activity involving ePHI</li>
                <li><strong>Integrity Controls:</strong> Mechanisms to ensure ePHI is not improperly altered or destroyed</li>
                <li><strong>Transmission Security:</strong> Encryption of ePHI in transit using industry-standard protocols (TLS 1.2+)</li>
                <li><strong>Authentication:</strong> Multi-factor authentication for remote access and sensitive systems</li>
                <li><strong>Encryption:</strong> AES-256 encryption for ePHI at rest and in transit</li>
                <li><strong>Vulnerability Management:</strong> Regular security patching, penetration testing, and vulnerability scanning</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>4. Breach Notification Compliance</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 16 }}>
                We maintain a comprehensive breach response and notification program:
              </p>
              <div className="legal-callout" style={{ marginBottom: 16 }}>
                <h3 className="text-navy fw-semibold" style={{ fontSize: 18, marginBottom: 12 }}>Breach Detection and Assessment</h3>
                <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                  <li>Continuous monitoring for potential security incidents</li>
                  <li>Prompt investigation and risk assessment of suspected breaches</li>
                  <li>Documentation of all incidents and breach determinations</li>
                </ul>
              </div>
              <div className="legal-callout" style={{ marginBottom: 16 }}>
                <h3 className="text-navy fw-semibold" style={{ fontSize: 18, marginBottom: 12 }}>Notification Procedures</h3>
                <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 8 }}>
                  In the event of a breach involving PHI:
                </p>
                <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                  <li>We notify affected healthcare providers without unreasonable delay and no later than 60 days after discovery</li>
                  <li>Notification includes details of the breach, types of information involved, mitigation actions, and recommended steps</li>
                  <li>We assist clients with their notification obligations to affected individuals and regulators</li>
                </ul>
              </div>
              <div className="legal-callout">
                <h3 className="text-navy fw-semibold" style={{ fontSize: 18, marginBottom: 12 }}>Mitigation and Prevention</h3>
                <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                  Following any security incident, we implement corrective actions to mitigate harm and prevent recurrence, including system patches, policy updates, and additional staff training.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>5. Business Associate Agreements</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                We execute a comprehensive Business Associate Agreement (BAA) with every client before accessing any PHI. Our BAA includes:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24, marginBottom: 16 }}>
                <li>Permitted and required uses and disclosures of PHI</li>
                <li>Commitments to implement appropriate safeguards</li>
                <li>Obligations to report security incidents and breaches</li>
                <li>Requirements for subcontractor agreements</li>
                <li>Provisions for PHI access, amendment, and accounting of disclosures</li>
                <li>Data return or destruction upon contract termination</li>
                <li>Compliance with HIPAA Security Rule requirements</li>
              </ul>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                Our BAA is available for review before service commencement and can be customized to meet specific client requirements while maintaining HIPAA compliance.
              </p>
            </section>

            {/* Section 6 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>6. Workforce Training and Awareness</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                Our employees receive comprehensive HIPAA training to ensure they understand and comply with privacy and security requirements:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                <li><strong>Initial Training:</strong> All new hires complete HIPAA training before accessing PHI</li>
                <li><strong>Annual Refreshers:</strong> Yearly training on HIPAA requirements, policies, and emerging threats</li>
                <li><strong>Role-Specific Training:</strong> Additional training tailored to job functions and access levels</li>
                <li><strong>Incident Response Training:</strong> Procedures for identifying and reporting security incidents</li>
                <li><strong>Ongoing Awareness:</strong> Regular security reminders, phishing simulations, and policy updates</li>
                <li><strong>Certification:</strong> Employees must pass assessments demonstrating HIPAA knowledge</li>
                <li><strong>Sanctions Policy:</strong> Clear consequences for HIPAA violations, from retraining to termination</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>7. Technology and Infrastructure Security</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 16 }}>
                Our technology infrastructure is designed with security and compliance as foundational principles. We implement commercially reasonable security measures including:
              </p>
              <div className="legal-callout" style={{ marginBottom: 12 }}>
                <h3 className="text-navy fw-semibold" style={{ fontSize: 18, marginBottom: 8 }}>🔒 Data Encryption</h3>
                <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                  All ePHI is encrypted at rest using AES-256 encryption and in transit using TLS 1.2 or higher. Encryption keys are managed using industry best practices with regular rotation.
                </p>
              </div>
              <div className="legal-callout" style={{ marginBottom: 12 }}>
                <h3 className="text-navy fw-semibold" style={{ fontSize: 18, marginBottom: 8 }}>🔑 Access Controls</h3>
                <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                  Role-based access controls (RBAC) ensure employees access only the PHI necessary for their job functions. Multi-factor authentication is required for all system access.
                </p>
              </div>
              <div className="legal-callout" style={{ marginBottom: 12 }}>
                <h3 className="text-navy fw-semibold" style={{ fontSize: 18, marginBottom: 8 }}>📊 Audit Logging</h3>
                <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                  Comprehensive logging of all access to and modifications of ePHI, with regular log reviews. Audit logs are retained for at least 6 years.
                </p>
              </div>
              <div className="legal-callout" style={{ marginBottom: 12 }}>
                <h3 className="text-navy fw-semibold" style={{ fontSize: 18, marginBottom: 8 }}>🛡️ Network Security</h3>
                <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                  Firewalls, intrusion detection/prevention systems (IDS/IPS), and network segmentation protect against unauthorized access. Regular vulnerability scans and penetration tests identify and address weaknesses.
                </p>
              </div>
              <div className="legal-callout">
                <h3 className="text-navy fw-semibold" style={{ fontSize: 18, marginBottom: 8 }}>💾 Backup and Disaster Recovery</h3>
                <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                  Daily encrypted backups with off-site storage and regular testing of disaster recovery procedures ensure business continuity and data availability.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>8. Regular Compliance Assessments</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                We maintain ongoing compliance through:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                <li><strong>Annual Risk Assessments:</strong> Comprehensive evaluation of potential risks and vulnerabilities to ePHI</li>
                <li><strong>Security Audits:</strong> Regular internal and third-party audits of our security controls and HIPAA compliance</li>
                <li><strong>Policy Reviews:</strong> Annual review and update of HIPAA policies and procedures</li>
                <li><strong>Compliance Monitoring:</strong> Continuous monitoring of regulatory changes and industry best practices</li>
                <li><strong>Third-Party Certifications:</strong> We maintain industry-standard practices and may pursue relevant security certifications (e.g., SOC 2, HITRUST)</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>9. Subcontractor Management</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                When we engage subcontractors who may have access to PHI, we:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                <li>Conduct due diligence to ensure they can meet HIPAA requirements</li>
                <li>Execute Business Associate Agreements with all subcontractors</li>
                <li>Require subcontractors to implement appropriate safeguards</li>
                <li>Periodically review subcontractor compliance and security practices</li>
                <li>Ensure subcontractors report security incidents promptly</li>
              </ul>
            </section>

            {/* Section 10 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>10. Reporting Security Concerns</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                We take all security concerns seriously. If you identify a potential security issue or have questions about our HIPAA compliance, please contact:
              </p>
              <div className="legal-callout">
                <p style={{ color: '#2B4278', fontSize: 16, marginBottom: 8 }}><strong>HIPAA Privacy and Security Officer</strong></p>
                <p style={{ color: '#5A6478', fontSize: 16, marginBottom: 4 }}>Jayesh Gadhvi</p>
                <p style={{ color: '#5A6478', fontSize: 16, marginBottom: 4 }}>Email: info@optibillanalytics.com</p>
                <p style={{ color: '#5A6478', fontSize: 16 }}>Website: www.optibillanalytics.com</p>
              </div>
            </section>

            {/* Commitment Statement */}
            <div className="cta-panel" style={{ padding: 32, marginTop: 40 }}>
              <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16, textAlign: 'center' }}>Our Ongoing Commitment</h2>
              <p style={{ fontSize: 16, lineHeight: 1.7, textAlign: 'center', margin: 0 }}>
                OptiBill Analytics remains committed to maintaining the highest standards of privacy and security. We continuously monitor regulatory developments, invest in security technologies, and enhance our compliance program to protect the PHI entrusted to us. Your confidence in our ability to safeguard sensitive health information is paramount, and we work diligently every day to earn and maintain that trust.
              </p>
            </div>

            {/* Footer Note */}
            <div style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid rgba(16,35,62,0.12)', textAlign: 'center' }}>
              <p style={{ color: '#5A6478', fontSize: 14, fontStyle: 'italic', marginBottom: 12 }}>
                This HIPAA Compliance Statement is effective as of March 21, 2026, and reflects our current practices and commitments.
              </p>
              <p style={{ color: '#5A6478', fontSize: 13, fontStyle: 'italic', background: '#f7fafc', padding: '12px 16px', borderRadius: 8, border: '1px solid #e6eef7', display: 'inline-block', textAlign: 'center' }}>
                <strong>Legal Disclaimer:</strong> This document does not constitute legal advice. Consult your attorney for specific guidance regarding your situation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HIPAA;
